import { useState, useCallback, useMemo } from 'react';
import type { ResearchMatrixState, ResearchMatrixActions } from '../types/research-matrix-types';

export const useResearchMatrixState = () => {
  const [state, setState] = useState<ResearchMatrixState>({
    showOnlyUserResearch: false, // Default to false so "All Research" is the default
    showFavorites: false,
    selectedResearchId: null,
    isLightboxOpen: false,
    currentResearchIndex: -1, // Add index tracking
  });

  // Memoized actions to prevent unnecessary re-renders
  const actions: ResearchMatrixActions = useMemo(() => ({
    handleFilterChange: (showUserResearch: boolean, showFavs: boolean) => {
      setState(prev => ({
        ...prev,
        showOnlyUserResearch: showUserResearch,
        showFavorites: showFavs,
        selectedResearchId: null,
        isLightboxOpen: false,
        currentResearchIndex: -1, // Reset index when filters change
      }));
    },

    handleResearchClick: (researchId: string) => {
      setState(prev => ({
        ...prev,
        selectedResearchId: researchId,
        isLightboxOpen: true,
        // Index will be calculated in the logic hook
      }));
    },

    handleCloseLightbox: () => {
      setState(prev => ({
        ...prev,
        selectedResearchId: null,
        isLightboxOpen: false,
        currentResearchIndex: -1,
      }));
    },

    handleToggleFavorite: async (researchId: string) => {
      // This will be implemented by the favorites hook
    },

    // Optimized navigation actions with minimal state updates
    handlePreviousResearch: (allResearch: any[]) => {
      setState(prev => {
        const newIndex = Math.max(0, prev.currentResearchIndex - 1);
        const newResearchId = allResearch[newIndex]?.id || null;
        
        // Only update if actually changing
        if (newIndex !== prev.currentResearchIndex && newResearchId !== prev.selectedResearchId) {
          return {
            ...prev,
            currentResearchIndex: newIndex,
            selectedResearchId: newResearchId,
          };
        }
        return prev;
      });
    },

    handleNextResearch: (allResearch: any[]) => {
      setState(prev => {
        const newIndex = Math.min(allResearch.length - 1, prev.currentResearchIndex + 1);
        const newResearchId = allResearch[newIndex]?.id || null;
        
        // Only update if actually changing
        if (newIndex !== prev.currentResearchIndex && newResearchId !== prev.selectedResearchId) {
          return {
            ...prev,
            currentResearchIndex: newIndex,
            selectedResearchId: newResearchId,
          };
        }
        return prev;
      });
    },

    setCurrentResearchIndex: (index: number) => {
      setState(prev => {
        // Only update if index actually changed
        if (index !== prev.currentResearchIndex) {
          return {
            ...prev,
            currentResearchIndex: index,
          };
        }
        return prev;
      });
    },
  }), []); // Empty dependency array since all functions are stable

  return {
    state,
    actions,
  };
};
